const VALUE_UNIT_REGEX = /-?\d*\.?\d+[a-z%]*/g;
const NUMBER_REGEX = /[\d.-]/g;

class Animation {
    constructor(animation, props) {
        this.name = animation;
        this.props = props;
        this.container = props.container || null;

        this.start = props.start ?? 0;
        this.end = props.end ?? 100;
        this.duration = props.duration ?? 1;
        this.normalizedProgress = 0;
        this.hasStarted = false;
        this.progressEnded = false;
        this.progress = 0;
    
        this.animations = {};
        this._cachedUnits = null;

        if (!this.container) {
            console.error('Container is required for animation:', animation);
            return;
        }

        this.container.addEventListener(this.props.events.onUpdate, (e) => {
            this.updateTimeline(e.detail.progress);
        });
        
        return this;
    }

    animate(element, props = false) {
        if (!element) {
            console.error('Element is required for animation:', this.name);
            return;
        }

        if (!props) {
            console.error('Props are required for animation:', this.name);
            return;
        }

        const elements = this.props.helpers.selector(element);
        const propsKeys = Object.keys(props);
        const $this = this;

        // Helper: detectar si un objeto es un descriptor de animación {from, to, unit}
        const isAnimationDescriptor = (obj) => {
            return obj !== null && 
                   typeof obj === 'object' && 
                   !Array.isArray(obj) && 
                   ('from' in obj || 'to' in obj || 'unit' in obj);
        };

        // Helper: detectar si un objeto contiene sub-propiedades (ej: transform)
        const isNestedObject = (obj) => {
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
            
            // Si tiene from/to/unit, no es nested
            if (isAnimationDescriptor(obj)) return false;
            
            // Verificar si contiene objetos {from, to, unit} o valores simples
            return Object.values(obj).some(val => 
                isAnimationDescriptor(val) || typeof val === 'number' || typeof val === 'string'
            );
        };

        // Función de parsing optimizada - SE EJECUTA SOLO UNA VEZ
        const parsePropValue = (value) => {
            // Funciones (callbacks)
            if (typeof value === 'function') {
                return { type: 'cta', value };
            }
            
            // Objetos con sub-propiedades (ej: transform, padding, margin)
            if (isNestedObject(value)) {
                const subProps = {};
                
                Object.entries(value).forEach(([key, val]) => {
                    if (isAnimationDescriptor(val)) {
                        const { from = 0, to = 0, unit = '' } = val;
                        subProps[key] = {
                            type: 'object',
                            from,
                            to,
                            unit,
                            change: to - from
                        };
                    } else if (typeof val === 'number') {
                        subProps[key] = {
                            type: 'number',
                            from: 0,
                            to: val,
                            unit: '',
                            change: val
                        };
                    } else {
                        // Valor estático
                        subProps[key] = {
                            type: 'static',
                            value: val
                        };
                    }
                });
                
                return {
                    type: 'nested',
                    subProps
                };
            }
            
            // Objetos con from/to (sintaxis directa)
            if (isAnimationDescriptor(value)) {
                const { from = 0, to = 0, unit = '' } = value;
                const change = to - from;
                return { 
                    type: 'object', 
                    from, 
                    to, 
                    unit,
                    change
                };
            }
            
            // Strings con valores numéricos (ej: "10px", "100%")
            if (typeof value === 'string') {
                const matches = value.match(VALUE_UNIT_REGEX);
                
                // String estático sin números
                if (!matches) {
                    return { type: 'static', value };
                }
                
                // Pre-parsear todos los valores numéricos
                const parsedValues = matches.map(m => {
                    const unitMatch = m.match(/[a-z%]+/);
                    return {
                        num: parseFloat(m),
                        unit: unitMatch ? unitMatch[0] : ''
                    };
                });
                
                return {
                    type: 'string',
                    template: value,
                    values: parsedValues,
                    valueCount: parsedValues.length,
                    valueIndex: 0
                };
            }
            
            // Números simples
            if (typeof value === 'number') {
                return { 
                    type: 'number', 
                    from: 0, 
                    to: value,
                    change: value,
                    unit: ''
                };
            }
            
            // Fallback
            return { type: 'static', value: String(value) };
        };

        // Pre-parsear TODAS las propiedades de TODOS los elementos
        elements.forEach((el, index) => {
            const parsedProps = {};
            
            propsKeys.forEach(prop => {
                const value = props[prop];
                
                // Arrays (ej: transform con múltiples funciones)
                if (Array.isArray(value)) {
                    parsedProps[prop] = {
                        type: 'array',
                        items: value.map(([key, val]) => ({
                            key,
                            parsed: parsePropValue(val)
                        }))
                    };
                } else {
                    parsedProps[prop] = parsePropValue(value);
                }
            });

            // Función de actualización OPTIMIZADA - SE EJECUTA EN CADA FRAME
            this.animations[index] = {
                element: el,
                parsedProps,
                update: () => {
                    const progress = $this.normalizedProgress;
                    
                    propsKeys.forEach(prop => {
                        const parsed = parsedProps[prop];
                        let newValue;

                        switch (parsed.type) {
                            case 'cta':
                                // Callback personalizado
                                newValue = parsed.value(progress, $this.progress);
                                break;

                            case 'nested':
                                // Objetos anidados (transform, padding, filter, etc)
                                const parts = [];
                                Object.entries(parsed.subProps).forEach(([key, subParsed]) => {
                                    let val;
                                    
                                    if (subParsed.type === 'object' || subParsed.type === 'number') {
                                        val = subParsed.from + (subParsed.change * progress);
                                    } else {
                                        val = subParsed.value;
                                    }
                                    
                                    // Formato específico según la propiedad
                                    if (prop === 'transform') {
                                        parts.push(`${key}(${val}${subParsed.unit})`);
                                    } else if (prop === 'filter') {
                                        parts.push(`${key}(${val}${subParsed.unit})`);
                                    } else {
                                        // Para padding, margin, etc: usar formato CSS estándar
                                        parts.push(`${val}${subParsed.unit}`);
                                    }
                                });
                                
                                newValue = parts.join(' ');
                                break;

                            case 'object':
                            case 'number':
                                // Interpolación numérica simple
                                newValue = parsed.from + (parsed.change * progress) + parsed.unit;
                                break;

                            case 'string':
                                // Interpolación de strings con múltiples valores
                                let valueIdx = 0;
                                newValue = parsed.template.replace(VALUE_UNIT_REGEX, () => {
                                    const v = parsed.values[valueIdx % parsed.valueCount];
                                    valueIdx++;
                                    return (v.num * progress) + v.unit;
                                });
                                break;

                            case 'array':
                                // Arrays de transformaciones
                                newValue = parsed.items.map(item => {
                                    const itemParsed = item.parsed;
                                    let val;
                                    
                                    if (itemParsed.type === 'object' || itemParsed.type === 'number') {
                                        val = itemParsed.from + (itemParsed.change * progress);
                                    } else if (itemParsed.type === 'string') {
                                        val = itemParsed.values[0].num * progress;
                                    } else {
                                        val = itemParsed.value;
                                    }
                                    
                                    const unit = itemParsed.unit || itemParsed.values?.[0]?.unit || '';
                                    return `${item.key}(${val}${unit})`;
                                }).join(' ');
                                break;

                            case 'static':
                                // Valor estático
                                newValue = parsed.value;
                                break;

                            default:
                                newValue = undefined;
                        }

                        // Aplicar el estilo solo si hay un valor válido
                        if (newValue !== undefined) {
                            el.style[prop] = newValue;
                        }
                    });
                }
            };
            
            // Ejecutar la primera actualización
            this.animations[index].update();
        });
    }

    onUpdate() {
        const { progress } = this;
        const previousProgress = this._previousProgress ?? 0;
        
        // Detectar dirección
        const isScrollingForward = progress > previousProgress;
        const isScrollingBackward = progress < previousProgress;

        // Determinar si llegamos al final o inicio
        this.progressEnded = progress === 1 ? 1 : (progress === 0 ? 0 : false);

        // Actualizar todas las animaciones
        Object.values(this.animations).forEach(anim => {
            anim.update();
        });

        // Callback de usuario
        if (this.props.onUpdate) {
            this.props.onUpdate.call(this);
        }
        
        this._previousProgress = progress;
    }

    onStart() {
        this.hasStarted = true;
        if (this.props.onStart) {
            this.props.onStart.call(this);
        }
    }

    onComplete() {
        this.hasCompleted = true;
        if (this.props.onComplete) {
            this.props.onComplete.call(this);
        }
    }

    onStartReverse() {
        this.hasStartedReverse = true;
        if (this.props.onStartReverse) {
            this.props.onStartReverse.call(this);
        }
    }

    onCompleteReverse() {
        this.hasStarted = false;
        this.hasCompleted = false;
        this.hasStartedReverse = false;
        if (this.props.onCompleteReverse) {
            this.props.onCompleteReverse.call(this);
        }
    }

    updateTimeline(progress) {
        const { start, end, duration } = this;

        // Cachear valores calculados
        if (!this._cachedUnits) {
            this._cachedUnits = {
                startUnit: start * 0.01,
                endUnit: end * 0.01
            };
        }
        
        const { startUnit, endUnit } = this._cachedUnits;
        const baseProgress = this.props.normalizedProgress(startUnit, endUnit, progress);

        // Aplicar easing basado en duration
        const adjustedProgress = duration !== 1 
            ? Math.pow(baseProgress, duration)
            : baseProgress;

        // Clamping optimizado
        this.progress = baseProgress;
        this.normalizedProgress = Math.max(0, Math.min(1, adjustedProgress));

        // Trigger de eventos
        if (this.progress > 0 && !this.hasStarted) {
            this.onStart();
        }

        this.onUpdate();

        if (progress === 1) {
            this.onComplete();
        }
    }
}

export default class Timeline {
    constructor(parent) {
        this.parent = parent;
        this.elements = parent.elements;
        this.customEvents = parent.customEvents;
        this.normalizedProgress = parent.normalizedProgress;
        this.helpers = parent.props.helpers;
    }

    add(name, props) {
        if (!name) {
            console.error('Animation name is required');
            return null;
        }
        
        if (!props) {
            console.error('Animation props are required');
            return null;
        }

        return new Animation(name, {
            ...props, 
            helpers: this.helpers,
            normalizedProgress: this.normalizedProgress,
            container: this.elements.container, 
            events: this.customEvents
        });
    }
}